import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
// import { LocalizationModel } from '../models';
import { EMAIL_REGEX, IIN_REGEX, LocalizationModel, ObjectUtils, ToFormGroup } from '@shared/core';

// current locale is key of the MyErrorsOptions
export interface FormValidationTranslationError {
  translation: string;
  params?: { [key: string]: string | number };
}

export type FormValidationErrors = {
  [errorKey: string]: string | FormValidationTranslationError;
};

export class FormValidators extends Validators {
  static override min(minValue: number): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined) {
        return null;
      }

      const sanitizedValue = value.toString().replace(/ /g, '').replace(',', '.');
      const numericValue = parseFloat(sanitizedValue);
      if (isNaN(numericValue) || numericValue < minValue) {
        return {
          min: {
            translation: `Значение должно быть больше ${ minValue }`,
            params: { value: minValue },
          },
        };
      }
      return null;
    };
  }
  static override max(maxValue: number): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined) {
        return null;
      }

      const sanitizedValue = value.toString().replace(/ /g, '').replace(',', '.');
      const numericValue = parseFloat(sanitizedValue);
      if (isNaN(numericValue) || numericValue > maxValue) {
        return {
          max: {
            translation: `Значение должно быть не больше ${ maxValue }`,
            params: { value: maxValue },
          },
        };
      }
      return null;
    };
  }

  static override minLength(minLength: number): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      if (Validators.minLength(minLength)(control) === null) {
        return null;
      }
      return {
        minLength: {
          translation: `Минимальная длина должна быть ${ minLength } символов`,
          params: { value: minLength },
        },
      };
    };
  }
  static override maxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      if (Validators.maxLength(maxLength)(control) === null) {
        return null;
      }
      return {
        maxLength: {
          translation: `Максимальная длина`,
          params: { value: maxLength },
        },
      };
    };
  }

  static fileType(acceptedTypes: string[], acceptedTypesText: string[]): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      // Support both File object and UploadedFile from NativeScript
      const fileType = value instanceof File ? value.type : value.fileType;

      if (!fileType || !acceptedTypes.includes(fileType)) {
        return {
          invalidFileType: {
            translation: `Разрешены типы файлов ${acceptedTypesText.join(', ')}`,
          },
        };
      }

      return null;
    };
  }

  static maxFileSize(maxSizeInMB: number): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      // Support both File object and UploadedFile from NativeScript
      const fileSize = value instanceof File ? value.size : value.fileSize;

      if (fileSize === undefined || fileSize === null) {
        return null;
      }

      const fileSizeInMB = fileSize / (1024 * 1024);
      if (fileSizeInMB > maxSizeInMB) {
        return {
          fileTooLarge: {
            translation: `Файл слишком большой. Максимум: ${maxSizeInMB} МБ (текущий: ${fileSizeInMB.toFixed(
              2,
            )} МБ)`,
          },
        };
      }

      return null;
    };
  }

  static requiredCheckbox(control: AbstractControl): FormValidationErrors | null {
    const value = control.value;

    if (value == null || value === false) {
      return { required: 'Обязательное поле' };
    }

    return null;
  }

  static override required(control: AbstractControl): FormValidationErrors | null {
    const value = control.value;

    if (
      value == null ||
      value === '' ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return { required: 'Обязательное поле' };
    }

    return null;
  }
  static iin(control: AbstractControl): FormValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    // Regular expression pattern for 12 digits
    const iinPattern = IIN_REGEX;

    // Check if the input matches the pattern
    if (!iinPattern.test(value)) {
      return { iin: 'form.error.iin' };
    }
    return null;
  }

  static override email(control: AbstractControl): FormValidationErrors | null {
    const value = control.value;

    if (value == null || value.length === 0) {
      return null;
    }

    // Regular expression
    const regexPattern = EMAIL_REGEX;

    // Check if the input matches the pattern
    if (!regexPattern.test(value)) {
      return { iin: 'form.error.email' };
    }
    return null;
  }

  // Custom validator function for checking if any control is not empty
  static translatableFieldRequiredValidator(
    control: FormGroup<ToFormGroup<LocalizationModel>>,
  ): FormValidationErrors | null {
    const controls = control?.controls;

    // Check if any control is invalid
    const langControls = Object.keys(control.value);
    const areAllControlsEmpty = langControls.every(key => {
      const value = control?.get(key)?.value;
      if (!value) {
        return true;
      }
      if (typeof value === 'string' && value.trim() === '') {
        return true;
      }

      return false;
    });

    if (areAllControlsEmpty) {
      // Set errors on all controls
      for (const key in controls) {
        const controlItem = ObjectUtils.getPropertyValue(controls, key);
        if (controlItem) {
          controlItem.setErrors({
            translatableControlInvalid: 'Обязательное поле',
          });
        }
      }
      // Set error on the group as well
      return { translatableControlsInvalid: 'Обязательное поле' };
    } else {
      // Clear errors from all controls
      for (const key in controls) {
        const controlItem = ObjectUtils.getPropertyValue(controls, key);
        if (controlItem && controlItem.errors && controlItem.errors['translatableControlInvalid']) {
          delete controlItem.errors['translatableControlInvalid'];
          // If there are no other errors, clear the errors object
          if (Object.keys(controlItem.errors).length === 0) {
            controlItem.setErrors(null);
          }
        }
      }
      // No errors on the group
      return null;
    }
  }

  static translatableFieldLengthValidator(length: number, type: 'min' | 'max'): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null; // Не валидируем, если это не FormGroup
      }

      const controls = control.controls;
      const langControls = Object.keys(control.value);

      // Check if any control is invalid based on type
      const isAnyControlInvalid = langControls.some(key => {
        const value = control.get(key)?.value;
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return false;
        }
        return type === 'min' ? value.length < length : value.length > length;
      });

      if (isAnyControlInvalid) {
        // Set errors on all controls
        for (const key in controls) {
          const controlItem = ObjectUtils.getPropertyValue(controls, key);
          if (controlItem) {
            controlItem.setErrors({
              [`translatableControl${type === 'min' ? 'Min' : 'Max'}Length`]: {
                translation: `form.error.${type}Length`,
                params: { value: length },
              },
            });
          }
        }
        // Set error on the group as well
        return {
          [`translatableControls${type === 'min' ? 'Min' : 'Max'}Length`]: {
            translation: `form.error.${type}Length`,
            params: { value: length },
          },
        };
      } else {
        // Clear errors from all controls
        for (const key in controls) {
          const controlItem = ObjectUtils.getPropertyValue(controls, key);
          if (
            controlItem &&
            controlItem.errors &&
            controlItem.errors[`translatableControl${type === 'min' ? 'Min' : 'Max'}Length`]
          ) {
            delete controlItem.errors[`translatableControl${type === 'min' ? 'Min' : 'Max'}Length`];
            if (Object.keys(controlItem.errors).length === 0) {
              controlItem.setErrors(null);
            }
          }
        }
        return null;
      }
    };
  }

  static arrayNotEmptyValidator(control: AbstractControl): FormValidationErrors | null {
    const value = control.value;
    if (Array.isArray(value) && value.length === 0) {
      return { arrayEmpty: 'form.error.arrayEmpty' };
    }
    return null;
  }

  static atLeastOneFieldRequired(
    errorMessage = 'Заполните хотя бы одно поле',
    errorKey = 'atLeastOneRequired',
  ): ValidatorFn {
    return (control: AbstractControl): FormValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }

      const controls = Object.keys(control.controls);
      const allEmpty = controls.every(key => {
        const controlValue = control.get(key)?.value;
        return !controlValue || controlValue === '0' || controlValue.toString().trim() === '';
      });

      if (allEmpty) {
        controls.forEach(key => {
          const fieldControl = control.get(key);
          fieldControl?.setErrors({ [errorKey]: errorMessage });
        });

        return { [errorKey]: errorMessage };
      } else {
        controls.forEach(key => {
          const fieldControl = control.get(key);
          if (fieldControl?.errors?.[errorKey]) {
            delete fieldControl.errors[errorKey];
            if (Object.keys(fieldControl.errors).length === 0) {
              fieldControl.setErrors(null);
            }
          }
        });
        return null;
      }
    };
  }
}
